/* =====================================================================
   The Crimson Season — behaviour.
   Vanilla, no dependencies. Every piece is an enhancement: with this file
   missing the page still reads, every anchor still jumps, every product
   sheet is still reachable as its own section of the document.
   ===================================================================== */
(() => {
  'use strict';
  try {
  const q  = (s, r = document) => r.querySelector(s);
  const qa = (s, r = document) => [...r.querySelectorAll(s)];
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The lighter version (open-items F46, v2) is the default since 18 Oct
     2026; the class is set in the page head before the first paint, and
     ?soft=0 leaves it off there to show the first version. */
  // ?intro=curtain shows the velvet curtain instead of the glitter opening
  if (/[?&]intro=curtain/.test(location.search)) document.documentElement.classList.add('curtain');

  /* ---- measurement ---------------------------------------------------
     Every meaningful action is published as a `pk:` custom event through
     Shopify's own analytics bus (Shopify.analytics.publish). Shopify hands
     those to the store's pixels — a custom pixel forwards them to GA4 and
     Meta (shopify/customer-events/crimson-pixel.js). Nothing here talks to
     Google or Meta directly, so the store's consent settings govern it.

     Each event says WHAT was done, WHERE on the page (top band, hero, a
     chapter, a dress row, the sheet, the line band, the footer…) and, when
     there is one, WHICH dress. No email, no free text a visitor typed.

     ?pkdebug=1 on any URL shows the events as they fire, for checking the
     page before it goes live.                                            */
  const DEBUG = /[?&]pkdebug=1/.test(location.search) ||
                (() => { try { return localStorage.getItem('pkdebug') === '1'; } catch (_) { return false; } })();
  if (/[?&]pkdebug=1/.test(location.search)) { try { localStorage.setItem('pkdebug', '1'); } catch (_) {} }
  if (/[?&]pkdebug=0/.test(location.search)) { try { localStorage.removeItem('pkdebug'); } catch (_) {} }
  let dbgBox = null;
  const debugShow = (name, data) => {
    console.info('[pk]', name, data);
    if (!dbgBox) {
      dbgBox = document.createElement('div');
      dbgBox.setAttribute('style', 'position:fixed;left:8px;top:90px;z-index:999;max-width:300px;' +
        'font:11px/1.4 ui-monospace,monospace;color:#fff;pointer-events:none');
      document.body.appendChild(dbgBox);
    }
    const row = document.createElement('div');
    row.setAttribute('style', 'background:rgba(20,14,12,.86);padding:6px 8px;margin-bottom:4px;border-left:3px solid #c33');
    row.textContent = name + '  ' + Object.entries(data).filter(([, v]) => v !== '' && v != null)
      .map(([k, v]) => k + '=' + v).join('  ');
    dbgBox.prepend(row);
    while (dbgBox.childNodes.length > 6) dbgBox.lastChild.remove();
  };

  /* With ?pkdebug=1, a badge in the corner reads Shopify's consent for this
     browser every second — so a banner can be tested in a clean (private)
     window: before any click it must read blank, after Deny "no", after
     Accept "yes". Added 17 Oct 2026 when a test browser read "yes" with no
     click and the cause could not be told apart from that browser's history. */
  if (DEBUG) {
    const badge = document.createElement('div');
    badge.setAttribute('style', 'position:fixed;right:8px;bottom:120px;z-index:999;' +
      'font:11px/1.4 ui-monospace,monospace;color:#fff;background:rgba(20,14,12,.9);' +
      'padding:6px 9px;border-left:3px solid #2a7;pointer-events:none;white-space:pre');
    badge.textContent = 'consent: loading…';
    const mount = () => document.body && !badge.isConnected && document.body.appendChild(badge);
    const read = () => {
      mount();
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.currentVisitorConsent !== 'function') return;
      try {
        const c = cp.currentVisitorConsent() || {};
        const v = (x) => (x === '' || x == null ? '—' : x);
        badge.textContent = 'consent  analytics ' + v(c.analytics) + '  marketing ' + v(c.marketing) +
          '\nbanner required ' + (cp.shouldShowBanner ? cp.shouldShowBanner() : '?') +
          '  region ' + (cp.getRegion ? cp.getRegion() : '?') +
          '\nclarity ' + ([...document.scripts].some((s) => /clarity\.ms\/tag/.test(s.src)) ? 'loaded' : 'not loaded');
      } catch (_) {}
    };
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
      }
    } catch (_) {}
    setInterval(read, 1000);
  }
  const track = (name, data = {}) => {
    const payload = Object.assign({ page: 'crimson-season' }, data);
    try {
      const S = window.Shopify;
      if (S && S.analytics && typeof S.analytics.publish === 'function') {
        const r = S.analytics.publish('pk:' + name, payload);
        if (r && typeof r.catch === 'function') r.catch(() => {});
      }
    } catch (_) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'pk_' + name }, payload)); } catch (_) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', 'pk_' + name); } catch (_) {}
    if (DEBUG) debugShow(name, payload);
  };
  window.__pkTrack = track;

  /* where on the page an element sits, in words a report can group by */
  const zoneOf = (el) => {
    if (el.closest('.ibar')) return 'top-band';
    if (el.closest('.act')) return 'action-bar';
    if (el.closest('.mylist')) return 'my-list';
    if (el.closest('.nav')) return 'nav';
    if (el.closest('.sheet')) return 'sheet';
    if (el.closest('.zoom')) return 'zoom';
    if (el.closest('.line')) return 'line-band';
    if (el.closest('.hero')) return 'hero';
    if (el.closest('.dress')) return 'dress-row';
    if (el.closest('.foot')) return 'footer';
    const sec = el.closest('section[id], [data-owner][id]');
    return sec ? sec.id : 'page';
  };
  const sheetStyle = () => {
    const h = location.hash.slice(1);
    return document.getElementById('style-' + h) ? h : '';
  };

  document.addEventListener('click', (e) => {
    const t = e.target.closest('a, button');
    if (!t) return;
    let name = t.dataset.track;
    if (!name) {
      const href = t.getAttribute('href') || '';
      if (/wa\.me/.test(href)) name = 'inquire';
      else if (t.matches('[data-tab], [data-ix]')) name = 'nav-section';
      else if (t.matches('[data-here]')) name = 'nav-menu';
      else if (t.matches('[data-style]')) name = 'open-dress';
      else if (t.matches('[data-gal-go], [data-gal-prev], [data-gal-next]')) name = 'gallery-frame';
      else if (t.matches('[data-gal-zoom]')) name = 'zoom';
      else if (t.matches('[data-zoom-src]')) name = 'accessory';
      else if (t.matches('.edcard')) name = 'edition-card';
      else if (t.matches('[data-gate-trigger]')) name = 'store-access';
      else if (t.matches('[data-sound]')) name = 'sound';
      else if (t.matches('[data-sheet-close], [data-zoom-close]')) name = 'close';
      else if (/instagram\.com/.test(href)) name = 'instagram';
      else if (/^mailto:/.test(href)) name = 'email';
      else if (/^#/.test(href)) name = 'anchor';
      else if (t.matches('.btn')) name = 'cta';
      else return;
    }
    const href = t.getAttribute('href') || '';
    track(name, {
      where: t.dataset.trackWhere || zoneOf(t),
      style: t.dataset.style || t.dataset.trackStyle || (t.closest('.sheet') ? sheetStyle() : ''),
      target: /^#/.test(href) ? href.slice(1) : '',
      label: ((t.getAttribute('aria-label') || t.textContent || '').replace(/\s+/g, ' ').trim()).slice(0, 60),
    });
  }, true);

  /* how far people read: each section once, and scroll depth in quarters */
  if ('IntersectionObserver' in window) {
    const seenSec = new Set();
    const sio = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (!en.isIntersecting || seenSec.has(en.target.id)) return;
        seenSec.add(en.target.id);
        track('section-view', { where: en.target.id });
        sio.unobserve(en.target);
      });
    }, { threshold: 0.35 });
    qa('main section[id], footer').forEach((el, i) => { if (!el.id) el.id = 'footer'; sio.observe(el); });
  }
  const depths = [25, 50, 75, 90, 100];
  let depthDone = 0;
  addEventListener('scroll', () => {
    if (depthDone >= depths.length) return;
    const max = document.documentElement.scrollHeight - innerHeight;
    const pct = max > 0 ? (scrollY / max) * 100 : 100;
    while (depthDone < depths.length && pct >= depths[depthDone] - 0.5) {
      track('scroll-depth', { where: String(depths[depthDone]) });
      depthDone++;
    }
  }, { passive: true });

  /* ---- navigator: stuck state + active tab ------------------------- */
  const nav = q('[data-nav]');
  const tabs = qa('[data-tab]');
  const byId = new Map(tabs.map(t => [t.getAttribute('href').slice(1), t]));

  if (nav) {
    const sentinel = q('[data-hero]');
    if (document.documentElement.classList.contains('soft')) {
      // soft v2 (agency, 18 Oct): solid from the first pixel of scroll, so
      // the bar never sits see-through over the hero's buttons
      const stick = () => nav.classList.toggle('is-stuck', scrollY > 4);
      addEventListener('scroll', stick, { passive: true }); stick();
    } else if (sentinel && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        nav.classList.toggle('is-stuck', !e.isIntersecting);
      }, { rootMargin: '-70px 0px 0px 0px' }).observe(sentinel);
    } else {
      nav.classList.add('is-stuck');
    }
  }

  /* ---- navigator on a phone: the section you are in, and the list --- */
  const here      = q('[data-here]');
  const hereLabel = q('[data-here-label]');
  const index     = q('[data-index]');
  const ixs       = qa('[data-ix]');

  if (here && index) {
    const shut = () => { index.hidden = true; here.setAttribute('aria-expanded', 'false'); };
    here.addEventListener('click', () => {
      const open = index.hidden;
      index.hidden = !open;
      here.setAttribute('aria-expanded', String(open));
    });
    index.addEventListener('click', (e) => { if (e.target.closest('[data-ix]')) shut(); });
    addEventListener('keydown', (e) => { if (e.key === 'Escape') shut(); });
    document.addEventListener('click', (e) => {
      if (!index.hidden && !e.target.closest('.nav')) shut();
    }, true);
  }

  /* ---- INQUIRE NOW: the first scroll turns the round button into a band */
  let ibarOn = false;
  const ibarCheck = () => {
    const on = scrollY > 8;
    if (on !== ibarOn) { ibarOn = on; document.documentElement.classList.toggle('ibar-on', on); }
  };
  addEventListener('scroll', ibarCheck, { passive: true });
  ibarCheck();

  /* ---- the full line, from anywhere on the page --------------------- */
  const line = q('[data-line]');
  if (line) {
    const btn = q('[data-line-toggle]', line);
    const panel = q('[data-line-panel]', line);
    const setLine = (open) => {
      panel.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('line-open', open);
    };
    btn.addEventListener('click', () => { setLine(panel.hidden); syncArrows(); });
    /* A mouse cannot swipe: on desktop the strip has arrows, and each one
       turns the strip by most of its own width. They grey out at the ends. */
    const rail = q('[data-line-rail]', line);
    const prev = q('[data-line-prev]', line), next = q('[data-line-next]', line);
    const syncArrows = () => {
      if (!rail) return;
      const max = rail.scrollWidth - rail.clientWidth - 2;
      prev && (prev.disabled = rail.scrollLeft <= 2);
      next && (next.disabled = rail.scrollLeft >= max);
    };
    const turn = (dir) => rail && rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: calm.matches ? 'auto' : 'smooth' });
    prev?.addEventListener('click', () => turn(-1));
    next?.addEventListener('click', () => turn(1));
    rail?.addEventListener('scroll', syncArrows, { passive: true });
    addEventListener('resize', syncArrows, { passive: true });
    /* a card opens the dress; the strip gets out of the way */
    panel.addEventListener('click', (e) => { if (e.target.closest('[data-style]')) setLine(false); });
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) setLine(false); });
    document.addEventListener('click', (e) => {
      if (!panel.hidden && !e.target.closest('[data-line]') && !e.target.closest('[data-sheet]')) setLine(false);
    });
  }

  /* ---- the reading line -------------------------------------------- */
  const prog = q('[data-prog]');
  if (prog) {
    let pending = false;
    const draw = () => {
      const max = document.documentElement.scrollHeight - innerHeight;
      const r = max > 0 ? Math.min(1, Math.max(0, scrollY / max)) : 0;
      prog.style.transform = 'scaleX(' + r.toFixed(4) + ')';
      pending = false;
    };
    addEventListener('scroll', () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(draw);
    }, { passive: true });
    addEventListener('resize', draw, { passive: true });
    draw();
  }

  if (byId.size && 'IntersectionObserver' in window) {
    /* The section whose top is nearest under the bar wins, so a short
       section between two tall ones still takes the tab. */
    const seen = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => seen.set(e.target.id, e));
      let best = null;
      seen.forEach((e) => {
        if (!e.isIntersecting) return;
        if (!best || e.boundingClientRect.top < best.boundingClientRect.top) best = e;
      });
      if (!best) return;
      tabs.forEach(t => t.classList.remove('is-on'));
      const tab = byId.get(best.target.dataset.owner || best.target.id);
      if (!tab) return;
      tab.classList.add('is-on');
      /* the phone navigator shows the same answer as a name */
      const win = best.target.dataset.owner || best.target.id;
      ixs.forEach(t => t.classList.toggle(
        'is-on', t.getAttribute('href').slice(1) === win));
      if (hereLabel) hereLabel.textContent = tab.textContent;
      /* keep the active tab in view inside the scrolling strip */
      const strip = tab.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const l = tab.offsetLeft - strip.clientWidth / 2 + tab.offsetWidth / 2;
        strip.scrollTo({ left: l, behavior: calm.matches ? 'auto' : 'smooth' });
      }
    }, { rootMargin: '-25% 0px -60% 0px', threshold: 0 });
    byId.forEach((_, id) => { const el = document.getElementById(id); if (el) io.observe(el); });
    /* chapters answer for the edition that owns them */
    qa('[data-owner]').forEach(el => io.observe(el));
  }

  /* ---- reveal ------------------------------------------------------ */
  const reveals = qa('[data-reveal]');
  if (reveals.length && 'IntersectionObserver' in window && !calm.matches) {
    const io = new IntersectionObserver((es) => {
      es.forEach(e => {
        if (!e.isIntersecting) return;
        const d = Number(e.target.dataset.reveal) || 0;
        setTimeout(() => e.target.classList.add('is-in'), d);
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    reveals.forEach(el => io.observe(el));
  } else {
    reveals.forEach(el => el.classList.add('is-in'));
  }

  /* ---- hero parallax ----------------------------------------------- */
  const heroImg = q('[data-parallax]');
  if (heroImg && !calm.matches) {
    let ticking = false;
    const move = () => {
      const y = Math.min(window.scrollY, window.innerHeight);
      heroImg.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
      ticking = false;
    };
    addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(move);
    }, { passive: true });
    move();
  }

  /* ---- the motion engine --------------------------------------------
     One loop drives every scrubbed element. Each element's target is a
     plain function of where it sits on the screen; what is shown eases a
     little behind that target, so the page glides instead of stepping.

     Two things the second version got wrong and this one must not do:
     it never pins anything, and it never waits for a threshold and then
     plays a timed animation. The reader's thumb sets the pace of
     everything, all the time, so the scroll can never feel broken.

     Only CSS custom properties and transforms are written; layout is
     never touched, so a frame costs almost nothing. The loop stops itself
     as soon as everything has caught up.                                */
  const root = document.documentElement;
  const clamp01 = (v) => v < 0 ? 0 : v > 1 ? 1 : v;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const FX = {
    /* the element moves against the scroll by up to `speed` px */
    lift: (el, p) => {
      const sp = Number(el.dataset.speed) || 60;
      el.style.transform = `translate3d(0, ${((0.5 - p) * sp).toFixed(1)}px, 0)`;
    },
    /* the frame opens from the middle as it rises into view */
    curtain: (el, p) => el.style.setProperty('--e', easeOut(clamp01((p - 0.04) / 0.34)).toFixed(4)),
    /* the paragraph darkens word by word between a fifth and three fifths */
    ink: (el, p) => el.style.setProperty('--p', clamp01((p - 0.14) / 0.40).toFixed(4)),
    /* the line of type slides sideways for as long as it is on screen */
    marquee: (el, p) => el.style.setProperty('--p', p.toFixed(4)),
  };

  const scenes = [];
  let looping = false;

  const progress = (r, vh) => (vh - r.top) / (vh + r.height);

  const frame = () => {
    const vh = innerHeight;
    let moving = false;
    for (const sc of scenes) {
      const r = sc.el.getBoundingClientRect();
      const target = clamp01(progress(r, vh));
      if (r.bottom < -vh * 0.5 || r.top > vh * 1.5) {
        /* far off screen: snap, so it never sweeps into view from a stale value */
        if (sc.cur !== target) { sc.cur = target; sc.fn(sc.el, target); }
        continue;
      }
      const d = target - sc.cur;
      if (Math.abs(d) > 0.0004) { sc.cur += d * 0.11; moving = true; }
      else sc.cur = target;
      sc.fn(sc.el, sc.cur);
    }
    if (moving) requestAnimationFrame(frame); else looping = false;
  };

  const kick = () => { if (!looping) { looping = true; requestAnimationFrame(frame); } };

  /* Snap everything to where it should be, with no easing. Used on load,
     when a parked tab comes back, and by anyone checking the page in a
     tool that does not paint. */
  const settle = () => {
    const vh = innerHeight;
    for (const sc of scenes) {
      sc.cur = clamp01(progress(sc.el.getBoundingClientRect(), vh));
      sc.fn(sc.el, sc.cur);
    }
  };

  if (!calm.matches) {
    qa('[data-fx]').forEach(el => {
      const fn = FX[el.dataset.fx];
      if (fn) scenes.push({ el, fn, cur: 0 });
    });
    if (scenes.length) {
      root.classList.add('motion');
      settle();
      addEventListener('scroll', kick, { passive: true });
      addEventListener('resize', () => { settle(); kick(); }, { passive: true });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) settle(); });
    }
  }
  window.__pkSettle = settle;

  /* ---- entrances: a line's words climb into place, once --------------- */
  const risers = qa('[data-rise]');
  const riseIn = (el) => el.classList.add('is-in');
  if (calm.matches || !('IntersectionObserver' in window)) {
    risers.forEach(riseIn);
  } else {
    const rio = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (!en.isIntersecting) return;
        if (en.target.dataset.rise === 'hero' && root.classList.contains('intro-on')) return;
        riseIn(en.target);
        rio.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0 });
    risers.forEach(el => rio.observe(el));
    /* a parked tab never reports intersections; catch up when it returns */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      risers.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.top < innerHeight * 0.88 && r.bottom > 0) riseIn(el);
      });
    });
  }

  /* ---- snow for the light opening (soft v2c, agency 18 Oct) -----------
     A thin band of glinting snowflakes at the very top: they drift down a
     little and fade before they reach the middle. Just before the logo's
     foil lands, a few of them stream down into the butterfly, as if the
     snow had run into the mark. Returns stream(rect) for the intro to call. */
  function snowfall(cv, layer) {
    const ctx = cv.getContext('2d');
    if (!ctx) return () => {};
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, band = 0;
    const size = () => {
      w = cv.clientWidth; h = cv.clientHeight; band = Math.max(80, Math.min(170, h * .17));
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    const cols = ['#C9A55C', '#DCC08A', '#B98A8A', '#A8484F'];
    const flake = (first) => ({
      x: Math.random() * w, y: first ? Math.random() * band * .9 - 8 : -8,
      v: 16 + Math.random() * 26, dx: (Math.random() - .5) * 10,
      r: 2.6 + Math.random() * 3.6, rot: Math.random() * 6.28, spin: (Math.random() - .5) * .8,
      c: cols[(Math.random() * cols.length) | 0], tw: Math.random() * 6.28
    });
    const n = Math.round(Math.min(34, Math.max(14, w / 38)));
    const flakes = Array.from({ length: n }, () => flake(true));
    const runs = [];                       // flakes on their way into the mark
    const draw = (x, y, r, rot, c, a) => {
      ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
      ctx.globalAlpha = a; ctx.strokeStyle = c; ctx.lineWidth = Math.max(.7, r * .16); ctx.lineCap = 'round';
      ctx.shadowColor = c; ctx.shadowBlur = r * 1.6;
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const an = k * Math.PI / 3, ca = Math.cos(an), sa = Math.sin(an);
        ctx.moveTo(0, 0); ctx.lineTo(ca * r, sa * r);
        const bx = ca * r * .55, by = sa * r * .55, br = r * .32;
        ctx.moveTo(bx, by); ctx.lineTo(bx + Math.cos(an + .7) * br, by + Math.sin(an + .7) * br);
        ctx.moveTo(bx, by); ctx.lineTo(bx + Math.cos(an - .7) * br, by + Math.sin(an - .7) * br);
      }
      ctx.stroke(); ctx.restore();
    };
    let last = performance.now(), feed = true;
    const frame = (now) => {
      if (!layer.isConnected) return;
      const dt = Math.min((now - last) / 1000, .05); last = now;
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        f.y += f.v * dt; f.x += f.dx * dt; f.rot += f.spin * dt;
        if (f.y > band) { if (feed) Object.assign(f, flake(false)); else continue; }
        const life = 1 - Math.max(0, f.y) / band;                 // fades as it comes down
        const tw = .7 + .3 * Math.sin(f.tw + now / 260);
        draw(f.x, f.y, f.r, f.rot, f.c, Math.max(0, life) * tw * .9);
      }
      for (let i = runs.length - 1; i >= 0; i--) {
        const p = runs[i], q = Math.min(1, Math.max(0, (now - p.t0) / p.dur));
        if (q >= 1) { runs.splice(i, 1); continue; }
        const e = q * q * (3 - 2 * q), u = 1 - e;                // ease, then a quadratic curve
        const x = u * u * p.x0 + 2 * u * e * p.cx + e * e * p.x1;
        const y = u * u * p.y0 + 2 * u * e * p.cy + e * e * p.y1;
        const a = q < .15 ? q / .15 : 1 - Math.max(0, (q - .7) / .3);
        draw(x, y, p.r * (1 - .6 * e), p.rot + e * 3, p.c, a);
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
    // stream(r): r is the butterfly's box on screen; the band stops feeding
    return (r) => {
      feed = false;
      const now = performance.now();
      for (let i = 0; i < 16; i++) {
        const x0 = Math.random() * w, y0 = band * (.25 + Math.random() * .55);
        const x1 = r.left + r.width * (.15 + Math.random() * .7), y1 = r.top + r.height * (.15 + Math.random() * .7);
        runs.push({ x0, y0, x1, y1, cx: (x0 + x1) / 2 + (Math.random() - .5) * w * .25, cy: y0 + (y1 - y0) * .2,
          r: 3 + Math.random() * 3, rot: Math.random() * 6.28, c: cols[i % 2], t0: now + i * 38, dur: 900 + Math.random() * 300 });
      }
    };
  }

  /* ---- the strip: every chapter of an edition in one row (F46) ---------
     Pages are a scroll-snap row, so a finger moves them; the arrows and the
     arrow keys move one page; "Meet …" on a chapter's first page moves on
     to its first dress. The row of chapter names follows the strip and
     jumps along it — nothing is swapped out, so nothing reloads. */
  qa('[data-book]').forEach((book) => {
    const tr = q('[data-book-track]', book);
    if (!tr) return;
    const pages = qa('.page', tr);
    const jumps = qa('[data-book-jump]', book);
    const prev = q('[data-book-prev]', book), next = q('[data-book-next]', book);
    const smooth = () => (calm.matches ? 'auto' : 'smooth');
    const lead = () => parseFloat(getComputedStyle(tr).scrollPaddingLeft) || 0;
    const at = () => {
      const x = tr.scrollLeft + lead();
      let best = 0, d = Infinity;
      pages.forEach((p, i) => { const dd = Math.abs(p.offsetLeft - x); if (dd < d) { d = dd; best = i; } });
      return best;
    };
    const goTo = (i) => {
      const p = pages[Math.max(0, Math.min(pages.length - 1, i))];
      if (p) tr.scrollTo({ left: p.offsetLeft - lead(), behavior: smooth() });
    };
    const upd = () => {
      const i = at();
      pages.forEach((p, k) => p.classList.toggle('is-current', k === i));
      const ch = pages[i] && pages[i].dataset.chapter;
      jumps.forEach((j) => {
        const on = j.dataset.bookJump === ch;
        if (on) j.setAttribute('aria-current', 'true'); else j.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === pages.length - 1;
    };
    tr.addEventListener('scroll', () => { cancelAnimationFrame(tr._r); tr._r = requestAnimationFrame(upd); }, { passive: true });
    if (prev) prev.addEventListener('click', () => goTo(at() - 1));
    if (next) next.addEventListener('click', () => goTo(at() + 1));
    qa('[data-book-next-inline]', tr).forEach((b) => b.addEventListener('click', () => goTo(pages.indexOf(b.closest('.page')) + 1)));
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(at() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(at() - 1); }
    });
    jumps.forEach((j) => j.addEventListener('click', () => {
      goTo(pages.findIndex((p) => p.id === j.dataset.bookJump));
    }));
    // a link to #chapter-n moves the strip to that chapter
    const hashed = () => {
      const i = pages.findIndex((p) => p.id && p.id === location.hash.slice(1));
      if (i >= 0) { tr.scrollTo({ left: pages[i].offsetLeft - lead(), behavior: 'auto' }); upd(); }
    };
    addEventListener('hashchange', hashed);
    addEventListener('resize', upd, { passive: true });
    hashed(); upd();
  });

  /* ---- buttons on the dresses in the group portraits (F46) -------------
     A click on the dot opens a small tag — the name, the line, the age and
     "Discover the dress", which opens the sheet. Click, not hover, on every
     screen: on a desktop the tag closed as the pointer travelled to it. */
  const hots = qa('.hot');
  if (hots.length) {
    hots.forEach((h) => {
      const tag = document.createElement('span');
      tag.className = 'hot__tag';
      const n = document.createElement('span'); n.className = 'hot__n'; n.textContent = h.dataset.n || '';
      const m = document.createElement('span'); m.className = 'hot__m'; m.textContent = h.dataset.m || '';
      const go = document.createElement('button');
      go.type = 'button'; go.className = 'hot__go'; go.dataset.style = h.dataset.hot;
      go.innerHTML = 'Discover the dress &rarr;';
      tag.append(n, m, go);
      h.appendChild(tag);
    });
    const closeAll = (except) => hots.forEach((h) => {
      if (h === except) return;
      h.classList.remove('is-open');
      const d = q('.hot__dot', h); if (d) d.setAttribute('aria-expanded', 'false');
    });
    document.addEventListener('click', (e) => {
      const dot = e.target.closest('.hot__dot');
      if (dot) {
        const h = dot.closest('.hot');
        const open = !h.classList.contains('is-open');
        closeAll(h);
        h.classList.toggle('is-open', open);
        dot.setAttribute('aria-expanded', String(open));
        return;
      }
      if (!e.target.closest('.hot__tag')) closeAll();
    });
    addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
    // a tag left open on a page that slides away closes with it
    qa('[data-book-track]').forEach((tr) => tr.addEventListener('scroll', () => closeAll(), { passive: true }));
  }

  /* ---- the drawn frames come in as they reach the screen (soft v2) ----- */
  const orns = qa('.orn');
  if (orns.length && root.classList.contains('soft')) {
    const draw = (svg) => {
      const rule = q('.orn__rule:not(.orn__rule--dot)', svg);
      if (rule && !rule.style.getPropertyValue('--len')) {
        // the stroke does not scale, so its dash is in screen pixels:
        // the rule's perimeter as drawn on screen, plus a little
        const b = rule.getBoundingClientRect();
        rule.style.setProperty('--len', Math.ceil(2 * (b.width + b.height) + 8) + 'px');
        void getComputedStyle(rule).strokeDashoffset;   // commit the start state
      }
      requestAnimationFrame(() => svg.classList.add('is-drawn'));
    };
    if ('IntersectionObserver' in window && !calm.matches) {
      const io = new IntersectionObserver((es) => es.forEach((en) => {
        if (en.isIntersecting) { draw(en.target); io.unobserve(en.target); }
      // starts once the frame's top edge is a fifth of the way up the screen:
      // seen happening, not finished before it arrives (8 % below the screen,
      // 18 Oct, was too early) nor half-way down it (30 % visible, too late)
      }), { threshold: 0, rootMargin: '0px 0px -20% 0px' });
      orns.forEach((o) => io.observe(o));
    } else orns.forEach((o) => o.classList.add('is-drawn'));
  }

  /* ---- the curtain before the season ---------------------------------- */
  const intro = q('[data-intro]');
  const heroTitle = q('[data-rise="hero"]');
  if (intro) {
    let seen = false;
    try { seen = sessionStorage.getItem('pk-intro') === '1'; } catch (_) {}
    /* F48, 19 Oct 2026: a buyer coming back within a month goes straight
       to the page — the film is a first meeting, not a toll gate */
    try { if (Date.now() - (+localStorage.getItem('pk-intro-at') || 0) < 30 * 864e5) seen = true; } catch (_) {}
    if (/[?&]intro=/.test(location.search)) seen = false;    // to show it again
    // soft v2: the glitter opening by default, the velvet curtain with
    // ?intro=curtain — both hold the name longer than the old lift
    const soft = root.classList.contains('soft');
    const curtain = soft && root.classList.contains('curtain');
    const film = soft && !curtain;         // the logo film opening
    const hold = curtain ? 1900 : 1750;
    const fade = curtain ? 1900 : film ? 1000 : 1100;
    if (seen || calm.matches) {
      intro.remove();
      if (heroTitle) riseIn(heroTitle);
    } else {
      try { sessionStorage.setItem('pk-intro', '1'); localStorage.setItem('pk-intro-at', String(Date.now())); } catch (_) {}
      root.classList.add('intro-on');
      let gone = false;
      const lift = () => {
        if (gone) return; gone = true;
        intro.classList.add('is-done');
        root.classList.remove('intro-on');
        if (heroTitle) setTimeout(() => riseIn(heroTitle), 380);
        setTimeout(() => intro.remove(), fade);
      };
      if (!film) setTimeout(lift, hold);
      else {
        /* The film's own clock runs the opening: the mark comes forward as
           it starts, the snow streams in at 0.85 s (the emboss), the foil
           lands at about 2 s, and the page opens 0.6 s after the film ends.
           No film within 1.2 s (blocked or slow) → the wordmark comes
           forward instead. Never longer than 6.5 s in any case. */
        const cv = q('.intro__glitter', intro);
        const stream = cv ? snowfall(cv, intro) : () => {};
        const box = q('[data-intro-logo]', intro);
        const vid = q('[data-intro-vid]', intro);
        let started = false, streamed = false;
        const fallback = () => {
          if (started) return;
          intro.classList.add('no-film');
          setTimeout(() => { const m = q('.intro__mark--berry', intro); if (m) stream(m.getBoundingClientRect()); }, 700);
          setTimeout(lift, 3200);
        };
        if (vid) {
          vid.addEventListener('playing', () => {
            if (started) return; started = true;
            box.classList.add('is-on');
          }, { once: true });
          vid.addEventListener('timeupdate', () => {
            if (streamed || vid.currentTime < .85) return; streamed = true;
            const r = box.getBoundingClientRect();
            // the butterfly sits in the upper middle of the film
            stream({ left: r.left + r.width * .33, top: r.top + r.height * .18, width: r.width * .34, height: r.height * .38 });
          });
          vid.addEventListener('ended', () => setTimeout(lift, 600), { once: true });
          vid.addEventListener('error', fallback, { once: true });
          const p = vid.play();
          if (p && p.catch) p.catch(fallback);
          setTimeout(fallback, 1200);
        } else fallback();
        setTimeout(lift, 6500);
      }
      intro.addEventListener('click', lift);
    }
  } else if (heroTitle) {
    riseIn(heroTitle);
  }

  /* ---- the "Discover" disc over a dress ------------------------------- */
  const dot = q('[data-cursor-dot]');
  if (dot && !calm.matches && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const label = q('[data-cursor-label]', dot);
    root.classList.add('has-cursor');
    let tx = -200, ty = -200, cx = tx, cy = ty, run = false;
    const follow = () => {
      cx += (tx - cx) * 0.2; cy += (ty - cy) * 0.2;
      dot.style.left = cx.toFixed(1) + 'px';
      dot.style.top = cy.toFixed(1) + 'px';
      if (Math.abs(tx - cx) + Math.abs(ty - cy) > 0.3) requestAnimationFrame(follow); else run = false;
    };
    addEventListener('pointermove', (e) => {
      tx = e.clientX; ty = e.clientY;
      if (!run) { run = true; requestAnimationFrame(follow); }
    }, { passive: true });
    document.addEventListener('pointerover', (e) => {
      const t = e.target.closest('[data-cursor]');
      dot.classList.toggle('is-on', !!t);
      if (t) label.textContent = t.dataset.cursor;
    });
    document.addEventListener('pointerleave', () => dot.classList.remove('is-on'));
  }


  /* ---- the sheet's gallery ------------------------------------------
     Delegated, because the sheet's contents are stamped out of a
     <template> every time one opens; nothing here is bound to a node
     that will still exist on the next open.                            */
  const galGo = (gal, i) => {
    const slides = qa('[data-gal-slide]', gal);
    const thumbs = qa('[data-gal-go]', gal);
    if (!slides.length) return;
    const n = ((i % slides.length) + slides.length) % slides.length;
    slides.forEach((el, k) => el.toggleAttribute('data-on', k === n));
    thumbs.forEach((el, k) => el.classList.toggle('is-on', k === n));
    const count = q('[data-gal-count]', gal);
    if (count) count.textContent = `${n + 1} / ${slides.length}`;
    const t = thumbs[n];
    const strip = t && t.closest('[data-gal-thumbs]');
    if (strip && strip.scrollWidth > strip.clientWidth) {
      const li = t.parentElement;
      strip.scrollTo({ left: li.offsetLeft - strip.clientWidth / 2 + li.offsetWidth / 2,
                       behavior: calm.matches ? 'auto' : 'smooth' });
    }
    gal._i = n;
  };

  document.addEventListener('click', (e) => {
    const gal = e.target.closest('[data-gal]');
    if (!gal) return;
    const thumb = e.target.closest('[data-gal-go]');
    if (thumb) { galGo(gal, Number(thumb.dataset.galGo)); return; }
    if (e.target.closest('[data-gal-prev]')) { galGo(gal, (gal._i || 0) - 1); return; }
    if (e.target.closest('[data-gal-next]')) { galGo(gal, (gal._i || 0) + 1); return; }
    if (e.target.closest('[data-gal-zoom]') || e.target.closest('[data-gal-slide]')) {
      const on = q('[data-gal-slide][data-on]', gal);
      if (on) openZoom(on.dataset.full, (q('figcaption', on) || {}).textContent || '');
    }
  });

  /* the accessories in the sheet open the same close look */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-zoom-src]');
    if (t) openZoom(t.dataset.zoomSrc, t.dataset.zoomCap || '');
  });

  /* ---- the close look ------------------------------------------------ */
  const zoom = q('[data-zoom]');
  let zPane, zImg, zCap, zScale = 1, zx = 0, zy = 0, zBase = 1;

  function openZoom(src, caption) {
    if (!zoom || !src) return;
    zPane = zPane || q('[data-zoom-pane]', zoom);
    zImg  = zImg  || q('[data-zoom-img]', zoom);
    zCap  = zCap  || q('[data-zoom-cap]', zoom);
    zImg.src = src;
    zImg.alt = caption;
    zCap.textContent = caption;
    zoom.hidden = false;
    document.documentElement.classList.add('zoom-open');
    zScale = zBase = 1; zx = zy = 0;
    applyZoom();
    q('[data-zoom-close]', zoom)?.focus();
  }

  function closeZoom() {
    if (!zoom || zoom.hidden) return;
    zoom.hidden = true;
    document.documentElement.classList.remove('zoom-open');
    if (zImg) zImg.removeAttribute('src');
  }

  function applyZoom() {
    if (!zImg) return;
    const r = zPane.getBoundingClientRect();
    const w = zImg.clientWidth * zScale, h = zImg.clientHeight * zScale;
    /* keep the frame inside the pane, or centred when it is smaller */
    const mx = Math.max(0, (w - r.width) / 2), my = Math.max(0, (h - r.height) / 2);
    zx = Math.min(mx, Math.max(-mx, zx));
    zy = Math.min(my, Math.max(-my, zy));
    zImg.style.transform =
      `translate(-50%, -50%) translate(${zx}px, ${zy}px) scale(${zScale})`;
    zImg.style.position = 'absolute';
    zImg.style.left = '50%';
    zImg.style.top = '50%';
  }

  if (zoom) {
    zPane = q('[data-zoom-pane]', zoom);
    zImg  = q('[data-zoom-img]', zoom);
    zCap  = q('[data-zoom-cap]', zoom);

    q('[data-zoom-close]', zoom).addEventListener('click', closeZoom);
    /* Escape belongs to the topmost layer only. Without stopping the event
       here the sheet's own handler runs in the same tick, sees the close
       look already shut, and closes the sheet underneath it too. */
    addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || zoom.hidden) return;
      closeZoom();
      e.stopImmediatePropagation();
    });

    zPane.addEventListener('wheel', (e) => {
      e.preventDefault();
      const k = Math.exp(-e.deltaY * 0.0016);
      zScale = Math.min(5, Math.max(1, zScale * k));
      if (zScale === 1) { zx = zy = 0; }
      applyZoom();
    }, { passive: false });

    zPane.addEventListener('dblclick', () => {
      zScale = zScale > 1.2 ? 1 : 2.4;
      if (zScale === 1) { zx = zy = 0; }
      applyZoom();
    });

    /* one pointer drags, two pinch */
    const pts = new Map();
    let startDist = 0, startScale = 1, lastX = 0, lastY = 0;

    zPane.addEventListener('pointerdown', (e) => {
      zPane.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, e);
      if (pts.size === 1) { lastX = e.clientX; lastY = e.clientY; zPane.classList.add('is-grabbing'); }
      if (pts.size === 2) {
        const [a, b] = [...pts.values()];
        startDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        startScale = zScale;
      }
    });

    zPane.addEventListener('pointermove', (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, e);
      if (pts.size === 2) {
        const [a, b] = [...pts.values()];
        const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        if (startDist) zScale = Math.min(5, Math.max(1, startScale * d / startDist));
        applyZoom();
      } else if (pts.size === 1) {
        zx += e.clientX - lastX;
        zy += e.clientY - lastY;
        lastX = e.clientX; lastY = e.clientY;
        applyZoom();
      }
    });

    const lift = (e) => {
      pts.delete(e.pointerId);
      if (!pts.size) zPane.classList.remove('is-grabbing');
      startDist = 0;
    };
    zPane.addEventListener('pointerup', lift);
    zPane.addEventListener('pointercancel', lift);
    addEventListener('resize', () => { if (!zoom.hidden) applyZoom(); }, { passive: true });
  }

  /* ---- product sheet ------------------------------------------------
     The sheet is a dialog over the page. Its state lives in the URL hash
     so Instagram can link straight to a style, and so the phone's back
     button closes it instead of leaving the page.                      */
  const sheet = q('[data-sheet]');
  if (sheet) {
    const panel = q('[data-sheet-panel]', sheet);
    let lastFocus = null;

    /* every dress in page order, for previous / next (F48) */
    const order = qa('template[id^="style-"]').map(t => t.id.slice(6));
    let cur = null;
    const open = (id, push) => {
      const src = document.getElementById('style-' + id);
      if (!src) return false;
      panel.querySelector('[data-sheet-content]').innerHTML = src.innerHTML;
      cur = id;
      const pos = q('[data-sheet-pos]', sheet);
      if (pos) pos.textContent = (order.indexOf(id) + 1) + ' / ' + order.length;
      document.dispatchEvent(new CustomEvent('pk-sheet', { detail: id }));
      const gal = panel.querySelector('[data-gal]');
      if (gal) galGo(gal, 0);
      track('dress-view', { style: id, where: push ? 'click' : 'link' });
      lastFocus = document.activeElement;
      sheet.classList.add('is-open');
      document.documentElement.classList.add('sheet-open');
      document.body.style.overflow = 'hidden';
      /* A frame is the right moment to start the slide, but a document
         that is not being painted — a backgrounded tab, a link opened in
         the background from Instagram — parks requestAnimationFrame, and
         the sheet would sit there unopened. The timer is the backstop;
         adding the class twice costs nothing. */
      const show = () => sheet.classList.add('is-on');
      requestAnimationFrame(show);
      setTimeout(show, 50);
      if (push && location.hash !== '#' + id) history.pushState({ pk: id }, '', '#' + id);
      const c = q('[data-sheet-close]', sheet);
      if (c) c.focus();
      return true;
    };

    const close = (pop) => {
      if (!sheet.classList.contains('is-open')) return;
      closeZoom();
      sheet.classList.remove('is-on');
      const done = () => {
        sheet.classList.remove('is-open');
        document.documentElement.classList.remove('sheet-open');
        document.body.style.overflow = '';
        panel.scrollTop = 0;
        if (lastFocus) lastFocus.focus();
      };
      calm.matches ? done() : setTimeout(done, 320);
      if (!pop && location.hash) history.pushState({}, '', location.pathname + location.search);
    };

    document.addEventListener('click', (e) => {
      const step = e.target.closest('[data-sheet-step]');
      if (step && cur) {
        const i = order.indexOf(cur), n = order.length;
        open(order[(i + Number(step.dataset.sheetStep) + n) % n], true);
        panel.scrollTop = 0;
        return;
      }
      const card = e.target.closest('[data-style]');
      if (card) { e.preventDefault(); open(card.dataset.style, true); return; }
      if (e.target.closest('[data-sheet-close]') || e.target.hasAttribute('data-sheet-scrim')) close(false);
    });
    addEventListener('keydown', (e) => {
      /* the close look is on top, so Escape belongs to it first */
      if (e.key === 'Escape') close(false);
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const gal = q('[data-gal]', sheet);
        if (gal && sheet.classList.contains('is-open') && (!zoom || zoom.hidden))
          galGo(gal, (gal._i || 0) + (e.key === 'ArrowRight' ? 1 : -1));
      }
    });
    addEventListener('popstate', () => {
      const id = location.hash.slice(1);
      if (id && document.getElementById('style-' + id)) open(id, false); else close(true);
    });

    /* swipe the sheet down to close, on touch */
    let y0 = null;
    panel.addEventListener('touchstart', (e) => {
      y0 = panel.scrollTop <= 0 ? e.touches[0].clientY : null;
    }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
      if (y0 === null) return;
      const dy = e.touches[0].clientY - y0;
      if (dy > 0) panel.style.transform = `translateY(${dy}px)`;
    }, { passive: true });
    panel.addEventListener('touchend', (e) => {
      if (y0 === null) return;
      const dy = e.changedTouches[0].clientY - y0;
      panel.style.transform = '';
      if (dy > 110) close(false);
      y0 = null;
    });

    /* arriving with a style already in the URL */
    const first = location.hash.slice(1);
    if (first && document.getElementById('style-' + first)) open(first, false);
  }

  /* ---- The dresses: the slide under the hero (open-items F48) ---------
     Filter by edition, a slide / grid switch, arrows above for a mouse, a
     thin line that fills as the slide moves. Every card is a small gallery
     (portrait front and back, studio front and back): a swipe on a phone;
     with a mouse it turns to the back pose on the first hover and shows
     its own arrows, and goes back to the front when the mouse leaves. */
  const lu = q('section.lu');
  if (lu) {
    const rail = q('[data-lu-track]', lu);
    const prev = q('[data-lu-prev]', lu), next = q('[data-lu-next]', lu);
    const bar = q('[data-lu-bar]', lu);
    const sync = () => {
      const w = rail.scrollWidth, c = rail.clientWidth, max = w - c;
      const shown = w > 0 ? c / w : 1;
      const r = max > 2 ? rail.scrollLeft / max : 1;
      if (bar) bar.style.transform = 'scaleX(' + Math.min(1, shown + r * (1 - shown)).toFixed(3) + ')';
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max - 2;
    };
    rail.addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', sync, { passive: true });
    const turn = (d) => rail.scrollBy({ left: d * rail.clientWidth * 0.8, behavior: calm.matches ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => turn(-1));
    if (next) next.addEventListener('click', () => turn(1));
    qa('[data-lu-chip]', lu).forEach((chip) => chip.addEventListener('click', () => {
      const f = chip.dataset.luChip;
      qa('[data-lu-chip]', lu).forEach((x) => x.setAttribute('aria-pressed', String(x === chip)));
      qa('.lu__item', lu).forEach((it) => { it.hidden = !(f === 'all' || it.dataset.ed === f); });
      rail.scrollLeft = 0; sync();
      track('cta', { where: 'line', label: 'Show: ' + chip.textContent.replace(/\s+/g, ' ').trim() });
    }));
    qa('[data-lu-view]', lu).forEach((b) => b.addEventListener('click', () => {
      const g = b.dataset.luView === 'grid';
      lu.classList.toggle('is-grid', g);
      qa('[data-lu-view]', lu).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      sync();
      track('cta', { where: 'line', label: g ? 'Grid view' : 'Slide view' });
    }));

    /* the small gallery in each card */
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    qa('.lu__card', lu).forEach((card) => {
      const pics = q('[data-lu-pics]', card);
      if (!pics) return;
      const n = pics.children.length;
      const dots = qa('.lu__dots i', card);
      const pp = q('[data-lu-pic="-1"]', card), np = q('[data-lu-pic="1"]', card);
      const at = () => Math.round(pics.scrollLeft / Math.max(1, pics.clientWidth));
      const mark = () => {
        const i = at();
        dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
        if (pp) pp.disabled = i <= 0;
        if (np) np.disabled = i >= n - 1;
      };
      const go = (i, smooth) => {
        i = Math.max(0, Math.min(n - 1, i));
        /* instant on a phone: the pictures box is overflow-hidden there (the
           swipe belongs to the slide) and a smooth scroll does not run on it */
        pics.scrollTo({ left: i * pics.clientWidth, behavior: smooth && fine && !calm.matches ? 'smooth' : 'auto' });
      };
      pics.addEventListener('scroll', mark, { passive: true });
      if (pp) pp.addEventListener('click', (e) => { e.stopPropagation(); go(at() - 1, true); });
      if (np) np.addEventListener('click', (e) => { e.stopPropagation(); go(at() + 1, true); });
      if (fine && n > 1) {
        card.addEventListener('mouseenter', () => { if (at() === 0) go(1, true); });
        card.addEventListener('mouseleave', () => go(0, true));
      }
      mark();
    });
    sync();
  }

  /* ---- long text folds to its opening lines (F48) -------------------- */
  qa('[data-more]').forEach((m) => {
    const b = q('[data-more-btn]', m);
    if (b) b.addEventListener('click', () => {
      m.classList.add('is-open');
      b.setAttribute('aria-expanded', 'true');
      track('cta', { label: b.textContent.trim() });
    });
  });

  /* ---- the buyer's list (F48) -----------------------------------------
     Dresses kept from their sheets, sent as one WhatsApp message with
     names and codes. Kept in this browser only (localStorage). */
  const ml = q('[data-mylist]');
  if (ml) {
    const info = (id) => {
      const t = document.getElementById('style-' + id);
      return t ? { n: t.dataset.name, c: t.dataset.code } : null;
    };
    let list = [];
    try { list = JSON.parse(localStorage.getItem('pk-list') || '[]'); } catch (_) {}
    if (!Array.isArray(list)) list = [];
    list = list.filter(info);
    const keep = () => { try { localStorage.setItem('pk-list', JSON.stringify(list)); } catch (_) {} };
    const pill = q('[data-mylist-toggle]', ml), pan = q('[data-mylist-panel]', ml);
    const items = q('[data-mylist-items]', ml), num = q('[data-mylist-n]', ml), send = q('[data-mylist-send]', ml);
    const waEl = q('.nav__wa');
    const base = waEl ? waEl.getAttribute('href').split('?')[0] : '';
    const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
    const shut = () => { pan.hidden = true; pill.setAttribute('aria-expanded', 'false'); };
    const draw = () => {
      ml.hidden = list.length === 0;
      num.textContent = String(list.length);
      items.innerHTML = list.map((id) => {
        const s = info(id);
        return '<li><span>' + esc(s.n) + '<small>' + esc(s.c) + '</small></span>' +
          '<button type="button" data-unkeep="' + esc(id) + '" aria-label="Remove ' + esc(s.n) + '">&times;</button></li>';
      }).join('');
      const msg = 'Hello, I would like the wholesale prices of these dresses from The Crimson Season:\n' +
        list.map((id) => { const s = info(id); return '- ' + s.n + ' (' + s.c + ')'; }).join('\n');
      send.href = base + '?text=' + encodeURIComponent(msg);
      qa('[data-keep]').forEach((b) => b.setAttribute('aria-pressed', String(list.includes(b.dataset.keep))));
      if (!list.length) shut();
    };
    document.addEventListener('click', (e) => {
      const k = e.target.closest('[data-keep]');
      if (k) {
        const id = k.dataset.keep, i = list.indexOf(id);
        if (i < 0) { list.push(id); track('cta', { where: 'sheet', style: id, label: 'Add to my list' }); }
        else list.splice(i, 1);
        keep(); draw(); return;
      }
      const u = e.target.closest('[data-unkeep]');
      if (u) { list = list.filter((x) => x !== u.dataset.unkeep); keep(); draw(); return; }
      if (e.target.closest('[data-mylist-toggle]')) {
        const open = pan.hidden; pan.hidden = !open; pill.setAttribute('aria-expanded', String(open)); return;
      }
      if (!pan.hidden && !e.target.closest('[data-mylist]')) shut();
    });
    document.addEventListener('pk-sheet', draw);
    draw();
  }

  /* ---- newsletter, prototype only ----------------------------------- */
  const news = q('[data-news]');
  if (news) {
    news.addEventListener('submit', (e) => {
      e.preventDefault();
      const ok = q('[data-news-ok]', news.parentElement);
      news.hidden = true;
      if (ok) { ok.hidden = false; ok.focus?.(); }
    });
  }

  /* ---- the way in ---------------------------------------------------
     Discretion, not security: the form is in the page for anyone who
     looks. The storefront password is the thing that protects the store. */
  const gate = q('[data-gate]');
  if (gate) {
    /* The trigger is the brand's butterfly, signed off at the end of the
       footer's last line — outside the panel it opens. */
    const trig = q('[data-gate-trigger]');
    const openGate = (focus = true) => {
      gate.classList.add('is-open');
      trig?.setAttribute('aria-expanded', 'true');
      if (focus) q('input[type="password"]', gate)?.focus();
    };
    trig?.addEventListener('click', () => {
      if (gate.classList.contains('is-open')) {
        gate.classList.remove('is-open'); trig.setAttribute('aria-expanded', 'false');
      } else openGate();
    });
    if (location.hash === '#trade') openGate();
    addEventListener('hashchange', () => { if (location.hash === '#trade') openGate(); });
    /* Shopify answers a wrong password by reloading the page with the
       error inside the form; bring the visitor straight back to it. */
    if (q('[data-gate-err]', gate)) {
      openGate();
      gate.scrollIntoView({ block: 'center' });
    }
  }

  /* after Shopify has recorded a sign-up, the page reloads with the thank-
     you in place of the form; take the visitor back to it */
  const newsForm = q('#pk-news') || q('[data-news]');
  newsForm?.addEventListener('submit', () => track('newsletter-submit', { where: 'newsletter' }));
  const done = q('[data-news-done]');
  if (done) {
    track('newsletter-success', { where: 'newsletter' });
    q('#newsletter')?.scrollIntoView({ block: 'start' });
    done.focus({ preventScroll: true });
  }

  /* ---- the season's sound -------------------------------------------
     Off on arrival, always. A browser will not start audible sound
     without a gesture, and a page that tries is a page people close. The
     button appears only once a source has actually loaded, so the track
     can be added later without touching the markup, and the page never
     offers sound it cannot play.                                       */
  const snd = q('[data-sound]');
  const au = q('[data-audio]');
  if (snd && au) {
    const VOL = 0.34, STEP = 0.04;
    let fade = null;

    const fadeTo = (target, then) => {
      clearInterval(fade);
      fade = setInterval(() => {
        const d = target - au.volume;
        if (Math.abs(d) <= STEP) {
          au.volume = target; clearInterval(fade); fade = null;
          if (then) then();
        } else {
          au.volume = Math.min(1, Math.max(0, au.volume + Math.sign(d) * STEP));
        }
      }, 45);
    };

    const setOn = (on) => {
      snd.setAttribute('aria-pressed', String(on));
      try { sessionStorage.setItem('pk-sound', on ? '1' : '0'); } catch (_) {}
      if (on) {
        au.volume = 0;
        au.play().then(() => fadeTo(VOL)).catch(() => snd.setAttribute('aria-pressed', 'false'));
      } else {
        fadeTo(0, () => au.pause());
      }
    };

    snd.addEventListener('click', () => setOn(snd.getAttribute('aria-pressed') !== 'true'));

    /* a tab nobody is looking at should not be playing */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !au.paused) { au.pause(); au._wasOn = true; }
      else if (!document.hidden && au._wasOn && snd.getAttribute('aria-pressed') === 'true') {
        au._wasOn = false; au.play().catch(() => {});
      }
    });

    /* Show the control only once a source answers. `preload="none"` means
       nothing is fetched until then, so this costs a HEAD-sized request
       and nothing when the track is not there yet. */
    const probe = q('source', au);
    if (probe) {
      /* "ok" is not enough: on a password-protected store every missing
         URL redirects to the password page, which answers 200 with HTML.
         Measured on the Shopify preview, 17 Oct 2026 — the button showed
         with no track behind it. Only an audio answer counts. */
      fetch(probe.src, { method: 'HEAD', redirect: 'manual' })
        .then(r => {
          const type = (r.headers.get('content-type') || '').toLowerCase();
          if (r.ok && type.startsWith('audio/')) snd.hidden = false;
        })
        .catch(() => {});
    }
  }

  /* ---- our cookie banner (open-items F44, F46) -------------------------
     Installed banners cannot record consent on a password page (the page
     lacks the token Shopify's own banner posts with — measured 17 Oct 2026).
     Shopify's Customer Privacy API in headless mode can: it records the
     choice through the Storefront API with the store's PUBLIC Storefront
     token (Headless channel, 18 Oct 2026). Public by design — it only reads
     what the storefront shows anyone; never put the private token here.
     The banner shows only when Shopify says this visitor still has to
     choose, and never over the opening. */
  const STOREFRONT_TOKEN = 'fb792d6fc5e80c4be65397d38902e474';
  /* Proof of consent (F44): every choice is also written as one line to a
     Google Sheet in the brand's Workspace, through an Apps Script web app
     (shopify/consent-log/Code.gs). Stored: a random consent id kept in this
     browser, the choices, how they were made, the banner's language and
     text version. No IP, no e-mail. Bump CONSENT_VERSION when the banner's
     words change, so each line says which text the visitor saw. */
  const CONSENT_LOG = 'https://script.google.com/macros/s/AKfycbypvcQDjbUq4FU89Vgr8aEKPXiGk6mXg4Zv8F4EFu3wpFe8KNjncrWWz8obmj4LEDPe/exec';
  const CONSENT_VERSION = '2026-10-18';
  const consentId = () => {
    const fresh = () => (window.crypto && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '')
      : (Math.random().toString(36).slice(2) + Date.now().toString(36)));
    try {
      let v = localStorage.getItem('pk-consent-id');
      if (!v) { v = fresh(); localStorage.setItem('pk-consent-id', v); }
      return v;
    } catch (_) { return fresh(); }
  };
  const logConsent = (analytics, marketing, how, lang) => {
    try {
      fetch(CONSENT_LOG, {
        method: 'POST', mode: 'no-cors', keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ id: consentId(), analytics: analytics ? 'yes' : 'no', marketing: marketing ? 'yes' : 'no',
          how, lang, version: CONSENT_VERSION, page: 'password' })
      }).catch(() => {});
    } catch (_) {}
  };
  const cbar = q('[data-consent]');
  if (cbar) {
    const langs = qa('[data-consent-lang]', cbar);
    const toTr = q('[data-consent-to="tr"]', cbar), toEn = q('[data-consent-to="en"]', cbar);
    let lang = /^tr/i.test(navigator.language || '') ? 'tr' : 'en';
    const setLang = (l) => {
      lang = l;
      langs.forEach((el) => { el.hidden = el.dataset.consentLang !== l; });
      if (toTr) toTr.hidden = l === 'tr';
      if (toEn) toEn.hidden = l === 'en';
      cbar.setAttribute('aria-labelledby', 'cbar-title-' + l);
    };
    setLang(lang);
    if (toTr) toTr.addEventListener('click', () => setLang('tr'));
    if (toEn) toEn.addEventListener('click', () => setLang('en'));

    const withCP = (fn) => {
      const S = window.Shopify;
      if (S && S.customerPrivacy && typeof S.customerPrivacy.setTrackingConsent === 'function') return fn(S.customerPrivacy);
      try {
        if (S && typeof S.loadFeatures === 'function') {
          S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
            if (!err && window.Shopify.customerPrivacy) fn(window.Shopify.customerPrivacy);
          });
        }
      } catch (_) {}
    };
    const current = (cp) => { try { return cp.currentVisitorConsent() || {}; } catch (_) { return {}; } };

    /* Microsoft Clarity waits for analytics consent. build.py prints only the
       project id (meta pk-clarity); the tag is loaded here, once, when the
       visitor has said yes — on this visit or an earlier one. Its own consent
       call tells it which storage it may use; a later "no" from Cookie
       settings switches it off again and asks it to drop its cookies. */
    const clarityMeta = q('meta[name="pk-clarity"]');
    const clarityId = clarityMeta && clarityMeta.content;
    const claritySet = (analytics, marketing) => {
      if (!clarityId) return;
      if (analytics && !window.clarity) {
        (function (c, l, a, r, i, t, y) {
          c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
          t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
          y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
        })(window, document, 'clarity', 'script', clarityId);
      }
      if (typeof window.clarity !== 'function') return;
      try {
        window.clarity('consentv2', {
          analytics_Storage: analytics ? 'granted' : 'denied',
          ad_Storage: marketing ? 'granted' : 'denied'
        });
        if (!analytics) window.clarity('consent', false);
      } catch (_) {}
    };
    withCP((cp) => {
      const c = current(cp);
      if (c.analytics === 'yes') claritySet(true, c.marketing === 'yes');
    });

    const settingsOpen = (on) => {
      qa('[data-consent-set]', cbar).forEach((el) => { el.hidden = !on; });
      qa('[data-consent-choose]', cbar).forEach((el) => { el.hidden = on; });
      qa('[data-consent-save]', cbar).forEach((el) => { el.hidden = !on; });
    };
    const show = (withSettings) => {
      withCP((cp) => {
        const c = current(cp);
        qa('[data-consent-cat="analytics"]', cbar).forEach((i) => { i.checked = c.analytics === 'yes'; });
        qa('[data-consent-cat="marketing"]', cbar).forEach((i) => { i.checked = c.marketing === 'yes'; });
      });
      settingsOpen(!!withSettings);
      cbar.hidden = false;
      requestAnimationFrame(() => cbar.classList.add('is-on'));
    };
    const hide = () => {
      cbar.classList.remove('is-on');
      setTimeout(() => { cbar.hidden = true; }, 450);
    };
    const save = (analytics, marketing, how) => {
      withCP((cp) => {
        cp.setTrackingConsent({
          analytics, marketing, preferences: analytics, sale_of_data: marketing,
          headlessStorefront: true,
          checkoutRootDomain: 'www.pembekelebeks.com',
          storefrontRootDomain: 'pembekelebeks.com',
          storefrontAccessToken: STOREFRONT_TOKEN
        }, (res) => {
          if (res && res.error) { console.warn('consent not recorded', res.error); return; }
          hide();
          claritySet(analytics, marketing);
          logConsent(analytics, marketing, how, lang);
          track('consent', { analytics: analytics ? 'yes' : 'no', marketing: marketing ? 'yes' : 'no' });
        });
      });
    };
    qa('[data-consent-all]', cbar).forEach((b) => b.addEventListener('click', () => {
      const yes = b.dataset.consentAll === '1';
      save(yes, yes, yes ? 'accept-all' : 'reject-all');
    }));
    qa('[data-consent-choose]', cbar).forEach((b) => b.addEventListener('click', () => settingsOpen(true)));
    qa('[data-consent-save]', cbar).forEach((b) => b.addEventListener('click', () => {
      const box = q('[data-consent-lang]:not([hidden])', cbar) || cbar;
      const a = !!(q('[data-consent-cat="analytics"]', box) || {}).checked;
      const m = !!(q('[data-consent-cat="marketing"]', box) || {}).checked;
      save(a, m, 'choose');
    }));
    qa('[data-consent-open]').forEach((b) => b.addEventListener('click', () => show(true)));

    // first visit: wait for the opening to end, then ask Shopify
    const firstLook = () => {
      if (root.classList.contains('intro-on')) return setTimeout(firstLook, 400);
      withCP((cp) => {
        let need = true;
        try { need = cp.shouldShowBanner(); } catch (_) {}
        const c = current(cp);
        const chosen = c.analytics === 'yes' || c.analytics === 'no';
        if (need && !chosen) show(false);
      });
    };
    setTimeout(firstLook, 600);
  }

  /* ---- legal panels: legal notice, privacy, cookies, EN / TR ----------
     Opened from the footer, from the newsletter's privacy link, or by
     #legal-notice / #privacy / #cookies in the address (a cookie banner can
     link straight to #cookies). Escape and the scrim close it. */
  const legal = q('[data-legal]');
  if (legal) {
    const panel = q('.legal__panel', legal);
    let cur = 'privacy', lang = 'en', back = null;
    const show = () => {
      qa('[data-legal-body]', legal).forEach(b => { b.hidden = !(b.dataset.legalBody === cur && b.dataset.lang === lang); });
      qa('[data-legal-tab]', legal).forEach(t => {
        t.setAttribute('aria-current', t.dataset.legalTab === cur ? 'true' : 'false');
        t.textContent = t.dataset[lang] || t.textContent;
      });
      qa('[data-legal-lang-set]', legal).forEach(b => b.setAttribute('aria-pressed', b.dataset.legalLangSet === lang ? 'true' : 'false'));
      q('.legal__scroll', legal).scrollTop = 0;
    };
    const open = (id, l) => {
      cur = id || cur; lang = l || lang; back = document.activeElement;
      legal.hidden = false; document.documentElement.style.overflow = 'hidden';
      show(); panel.focus();
      track('legal-open', { target: cur, label: lang });
    };
    const close = () => {
      legal.hidden = true; document.documentElement.style.overflow = '';
      if (/^#(legal-notice|privacy|cookies)$/.test(location.hash)) history.replaceState(null, '', location.pathname + location.search);
      if (back && back.focus) back.focus();
    };
    document.addEventListener('click', (e) => {
      const o = e.target.closest('[data-legal-open]');
      if (o) { e.preventDefault(); open(o.dataset.legalOpen, o.dataset.legalLang); return; }
      if (legal.hidden) return;
      const t = e.target.closest('[data-legal-tab]');
      if (t) { cur = t.dataset.legalTab; show(); return; }
      const l = e.target.closest('[data-legal-lang-set]');
      if (l) { lang = l.dataset.legalLangSet; show(); return; }
      if (e.target.closest('[data-legal-close]')) close();
    });
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && !legal.hidden) { e.stopImmediatePropagation(); close(); } }, true);
    const fromHash = () => {
      const m = /^#(legal-notice|privacy|cookies)$/.exec(location.hash);
      if (m) open(m[1], /tr/i.test(navigator.language || '') ? 'tr' : 'en');
    };
    addEventListener('hashchange', fromHash);
    fromHash();
  }

  } catch (err) {
    /* Take the pre-animation states off, so a broken script can never
       leave a word or a frame hidden. */
    document.documentElement.classList.remove('js', 'motion', 'intro-on', 'has-cursor');
    console.error(err);
  }
})();
